"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="29010" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 animate-pulse rounded bg-primary/10" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-sans font-semibold tracking-wide text-foreground/80 transition-colors hover:text-primary after:absolute after:bottom-[-4px] after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} className="h-8 w-full animate-pulse rounded bg-muted" />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-sans font-medium text-foreground/90 transition-colors hover:text-primary" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="pt-4">
        <Button asChild className="w-full bg-primary text-primary-foreground shadow-[0_0_15px_rgba(132,23,54,0.3)] hover:bg-primary/90">
          <Link to="#" onClick={onClose}>
            Connect
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 bg-background text-foreground", isScrolled ? "bg-background/80 shadow-md backdrop-blur-md border-b border-border/40" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        {}
        <Link to="/HomePage" className="flex items-center gap-2 outline-none">
          <div className="relative flex items-center justify-center">
            <span className="font-sans font-semibold tracking-[0.2em] uppercase text-lg text-foreground">
              NATHAN HARRINGTON
            </span>
          </div>
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="16" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:block">
          <Button asChild className="bg-primary text-primary-foreground shadow-[0_0_15px_rgba(132,23,54,0.2)] transition-all hover:bg-primary/90 hover:shadow-[0_0_20px_rgba(132,23,54,0.4)] border border-primary/20 animate-pulse">
            <Link to="#">Connect</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="text-foreground md:hidden hover:bg-accent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {}
      <div className={cn("absolute left-0 top-full w-full overflow-hidden bg-background/95 backdrop-blur-xl transition-all duration-300 md:hidden border-b border-border/20", isMobileMenuOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0")}>
        <MenuProvider menu_id="16" className="mobile-menu">
          <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </header>;
}